import { useCallback, useEffect, useState } from "react";
import { videos } from "@/mocks/videos";
import type { Video } from "@/utils/videoUtils";

const STORAGE_KEY = "d91_favorites";
const EVENT_NAME = "d91-favorites-changed";

export interface FavoriteRecord {
  id: string;
  savedAt: number;
}

export interface FavoriteVideo extends Video {
  savedAt: number;
}

function readFavorites(): FavoriteRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is FavoriteRecord =>
        !!item && typeof item.id === "string" && typeof item.savedAt === "number",
    );
  } catch {
    return [];
  }
}

function writeFavorites(items: FavoriteRecord[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // storage unavailable — keep in-memory state only
  }
  window.dispatchEvent(new Event(EVENT_NAME));
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<FavoriteRecord[]>(() => readFavorites());

  useEffect(() => {
    const sync = () => setFavorites(readFavorites());
    window.addEventListener(EVENT_NAME, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT_NAME, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const isFavorite = useCallback(
    (id: string) => favorites.some((item) => item.id === id),
    [favorites],
  );

  const toggleFavorite = useCallback((id: string) => {
    const current = readFavorites();
    const exists = current.some((item) => item.id === id);
    const next = exists
      ? current.filter((item) => item.id !== id)
      : [{ id, savedAt: Date.now() }, ...current];
    writeFavorites(next);
    return !exists;
  }, []);

  const removeFavorite = useCallback((id: string) => {
    writeFavorites(readFavorites().filter((item) => item.id !== id));
  }, []);

  const clearFavorites = useCallback(() => {
    writeFavorites([]);
  }, []);

  const favoriteVideos: FavoriteVideo[] = favorites
    .map((record) => {
      const video = videos.find((item) => item.id === record.id);
      return video ? { ...video, savedAt: record.savedAt } : null;
    })
    .filter((item): item is FavoriteVideo => item !== null);

  return {
    favorites,
    favoriteVideos,
    count: favorites.length,
    isFavorite,
    toggleFavorite,
    removeFavorite,
    clearFavorites,
  };
}